<script lang="ts">
  import { store } from '../store.svelte';
  import { visited } from '../visited.svelte';
  import { REG, byId } from '../data/grimoire';
  import type { RegId } from '../data/grimoire';

  const regs: Array<{ id: 'all' | RegId; label: string }> = [
    { id: 'all', label: 'Все' },
    { id: 'reg-1', label: 'I' },
    { id: 'reg-2', label: 'II' },
    { id: 'reg-3', label: 'III' },
    { id: 'reg-4', label: 'IV' },
  ];

  function countText() {
    const n = store.visibleCount;
    if (!store.hasFilter) return `${n} узлов`;
    return `${n} найдено`;
  }

  let showClear = $state(false);
  function clearVisited() {
    if (confirm('Сбросить отметки просмотренного?')) visited.clear();
  }

  // Esc inside the search box clears the query (but doesn't close anything
  // since there's nothing to close — keeps typing flow unbroken).
  function onInputKey(ev: KeyboardEvent) {
    if (ev.key === 'Escape' && store.query) {
      ev.preventDefault();
      ev.stopPropagation();
      store.query = '';
      document.getElementById('findInput')?.focus();
    }
  }

  // Pick a random currently-visible entry and open it. When filter is active,
  // samples only from the visible pool — fun way to discover within a theme.
  function randomEntry() {
    const pool: string[] = [];
    for (const r of REG) for (const e of r.entries) if (store.isVisible(e.id)) pool.push(e.id);
    if (!pool.length) return;
    const id = pool[Math.floor(Math.random() * pool.length)];
    // Scroll the chapters area to roughly where the entry lives so the
    // opening feels grounded rather than random-modal-out-of-nowhere.
    store.open(id);
  }
</script>

<div class="find" id="find">
  <div class="wrap find__inner">
    <div class="find__field">
      <input
        id="findInput"
        type="search"
        placeholder="Искать способность, термин, имя…"
        aria-label="Поиск по гримуару"
        autocomplete="off"
        value={store.query}
        oninput={(e) => (store.query = (e.currentTarget as HTMLInputElement).value)}
        onkeydown={onInputKey}
      />
      {#if store.query}
        <button
          class="find__clear"
          type="button"
          aria-label="Очистить поиск"
          title="Очистить (Esc)"
          onclick={() => {
            store.query = '';
            document.getElementById('findInput')?.focus();
          }}
        >✕</button>
      {/if}
    </div>
    <div class="find__chips">
      {#each regs as r (r.id)}
        <button class="fch" class:on={store.activeReg === r.id} onclick={() => store.setReg(r.id)}>
          {r.label}
        </button>
      {/each}
    </div>
    <button
      class="fch ftrain"
      class:on={store.trainOnly}
      aria-pressed={store.trainOnly}
      title="Только то, что реально тренируется"
      onclick={() => store.toggleTrain()}
    >
      ▲ тренируемое
    </button>
    <button
      class="fch frand"
      title="Случайный узел"
      aria-label="Случайный узел"
      onclick={randomEntry}
    >⚂</button>
    <div class="find__meta">
      {#if visited.size > 0}
        <button
          class="visited-pill"
          class:active={showClear}
          onmouseover={() => (showClear = true)}
          onmouseleave={() => (showClear = false)}
          onfocus={() => (showClear = true)}
          onblur={() => (showClear = false)}
          onclick={clearVisited}
          title="Сбросить отметки"
        >
          {visited.size} ∙
        </button>
      {/if}
      <div class="find__count">{countText()}</div>
    </div>
  </div>
</div>

<style>
  .find__field {
    position: relative;
    flex: 1;
    min-width: 170px;
  }
  .find__field input {
    width: 100%;
  }
  .find__clear {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 0;
    background: rgba(231, 223, 206, 0.06);
    color: var(--ink-dim);
    cursor: pointer;
    font-size: 0.7rem;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s, background 0.2s;
  }
  .find__clear:hover {
    color: var(--ink);
    background: rgba(231, 223, 206, 0.12);
  }
  .find__clear:focus-visible {
    outline: 1px solid var(--mercury);
    outline-offset: 1px;
  }
  /* Make room for the ✕ inside the input. */
  :global(#findInput) {
    padding-right: 34px !important;
  }

  .find__meta {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
  .visited-pill {
    font-family: var(--mono);
    font-size: 0.68rem;
    letter-spacing: 0.1em;
    color: var(--mercury);
    background: rgba(86, 202, 190, 0.08);
    border: 1px solid var(--mercury-dim);
    border-radius: 999px;
    padding: 4px 9px;
    cursor: pointer;
    transition: 0.2s;
    white-space: nowrap;
  }
  .visited-pill:hover,
  .visited-pill.active {
    color: var(--rubedo);
    border-color: var(--rubedo);
    background: rgba(200, 68, 59, 0.08);
  }
  .frand {
    color: var(--gold);
    font-size: 0.85rem;
    line-height: 1;
    padding: 7px 10px;
    transition: transform 0.4s, color 0.2s, border-color 0.2s;
  }
  .frand:hover {
    color: var(--ink);
    border-color: var(--gold);
    transform: rotate(60deg);
  }
</style>
